import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';

import { useCoverLotItemMeta } from '@/hooks/useCoverLotItemMeta';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogCoverLotsTransformed } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { useTrackItemCardClicked } from '@/utils/amplitude/analytics';

import type { CoverLotData } from '../../types';

import { LARGE_IMAGE_SIZE } from '../../CoverLotImage';
import { DesktopImageCluster } from './components/DesktopImageCluster/DesktopImageCluster';
import { DoneOverlay } from './components/DoneOverlay/DoneOverlay';
import { MobileImageCluster } from './components/MobileImageCluster/MobileImageCluster';

type Props = {
    catalogId: number;
    isCatalogDone: boolean;
};

export const CoverLotsImageCluster = ({ catalogId, isCatalogDone }: Props) => {
    const trackItemCardClicked = useTrackItemCardClicked();
    const isMobile = useWindowIsMobile();

    const {
        primaryImage,
        primaryImageHighDpi,
        primaryItemId,
        primaryItemTitle,
        secondaryImage,
        secondaryImageHighDpi,
        secondaryItemId,
        secondaryItemTitle,
        tertiaryImage,
        tertiaryImageHighDpi,
        tertiaryItemId,
        tertiaryItemTitle,
    } = useAppSelector((state) => getCatalogCoverLotsTransformed(state, catalogId, LARGE_IMAGE_SIZE));

    const primaryMeta = useCoverLotItemMeta(primaryItemId);
    const secondaryMeta = useCoverLotItemMeta(secondaryItemId);
    const tertiaryMeta = useCoverLotItemMeta(tertiaryItemId);

    if (!primaryItemId) {
        return null;
    }

    // `secondaryItemId`/`tertiaryItemId` are 0 when that slot has no real cover lot — unlike
    // `secondaryImage`/`tertiaryImage`, which always resolve to a truthy placeholder URL even when empty.
    const coverLots = [
        {
            catalogId: primaryMeta.catalogId,
            image: primaryImage,
            imageHighDpi: primaryImageHighDpi,
            itemId: primaryItemId,
            promoted: primaryMeta.promoted,
            title: primaryItemTitle,
            url: primaryMeta.url,
        },
        secondaryItemId && {
            catalogId: secondaryMeta.catalogId,
            image: secondaryImage,
            imageHighDpi: secondaryImageHighDpi,
            itemId: secondaryItemId,
            promoted: secondaryMeta.promoted,
            title: secondaryItemTitle,
            url: secondaryMeta.url,
        },
        tertiaryItemId && {
            catalogId: tertiaryMeta.catalogId,
            image: tertiaryImage,
            imageHighDpi: tertiaryImageHighDpi,
            itemId: tertiaryItemId,
            promoted: tertiaryMeta.promoted,
            title: tertiaryItemTitle,
            url: tertiaryMeta.url,
        },
    ].filter((lot): lot is CoverLotData => Boolean(lot));

    const doneOverlay = (
        <DoneOverlay
            isCatalogDone={isCatalogDone}
            isMobile={isMobile}
        />
    );

    if (isMobile) {
        return (
            <MobileImageCluster
                coverLots={coverLots}
                doneOverlay={doneOverlay}
                trackItemCardClicked={trackItemCardClicked}
            />
        );
    }

    return (
        <DesktopImageCluster
            coverLots={coverLots}
            doneOverlay={doneOverlay}
            trackItemCardClicked={trackItemCardClicked}
        />
    );
};
